

import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    IconButton,
    Snackbar,
    Alert
} from '@mui/material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from 'auth/Auth';
import colors from "assets/theme/base/colors";

const NewOrderCancellationModal = ({
    isOpen,
    onClose,
    onSave,
    travelOrderId = null
}) => {
    const { axiosInstance, user } = useAuth();
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        invoice_id: '',
        payment_transaction_id: '',
        travel_invoice_id: '',
        travel_order_id: travelOrderId || '',
        approval_status: 'Not Submitted',
        requested_on: new Date().toISOString().slice(0, 16),
        order_line_item_id: '',
        reject_reason: '',
        cancellation_type: '',
        status: 'Active',
        comments: '',
        amount_deduction: '',
        total_amount: '',
        refund_amount: ''
    });

    const [formErrors, setFormErrors] = useState({});
    const [invoices, setInvoices] = useState([]);
    const [paymentTransactions, setPaymentTransactions] = useState([]);
    const [travelInvoices, setTravelInvoices] = useState([]);
    const [travelOrders, setTravelOrders] = useState([]);
    const [orderItems, setOrderItems] = useState([]);
    const [loadingInvoices, setLoadingInvoices] = useState(false);
    const [loadingPaymentTransactions, setLoadingPaymentTransactions] = useState(false);
    const [loadingTravelInvoices, setLoadingTravelInvoices] = useState(false);
    const [loadingTravelOrders, setLoadingTravelOrders] = useState(false);
    const [loadingOrderItems, setLoadingOrderItems] = useState(false);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

    useEffect(() => {
        if (isOpen) {
            if (!travelOrderId) {
                fetchTravelOrders();
            }
            fetchInvoices();
            fetchPaymentTransactions();
            fetchTravelInvoices();
        }
    }, [isOpen, travelOrderId]);

    useEffect(() => {
        if (formData.travel_order_id) {
            fetchOrderItems(formData.travel_order_id);
        }
    }, [formData.travel_order_id]);

    const fetchInvoices = async () => {
        setLoadingInvoices(true);
        try {
            const response = await axiosInstance.get('/invoices/');
            setInvoices(response.data || []);
        } catch (error) {
            console.error('Error fetching invoices:', error);
        } finally {
            setLoadingInvoices(false);
        }
    };

    const fetchPaymentTransactions = async () => {
        setLoadingPaymentTransactions(true);
        try {
            const response = await axiosInstance.get('/payment-transactions/');
            setPaymentTransactions(response.data || []);
        } catch (error) {
            console.error('Error fetching payment transactions:', error);
        } finally {
            setLoadingPaymentTransactions(false);
        }
    };

    const fetchTravelInvoices = async () => {
        setLoadingTravelInvoices(true);
        try {
            const response = await axiosInstance.get('/travel-invoices/');
            setTravelInvoices(response.data || []);
        } catch (error) {
            console.error('Error fetching travel invoices:', error);
        } finally {
            setLoadingTravelInvoices(false);
        }
    };

    const fetchTravelOrders = async () => {
        setLoadingTravelOrders(true);
        try {
            const response = await axiosInstance.get('/travel-orders/');
            setTravelOrders(response.data || []);
        } catch (error) {
            console.error('Error fetching travel orders:', error);
        } finally {
            setLoadingTravelOrders(false);
        }
    };

    const fetchOrderItems = async (orderId) => {
        setLoadingOrderItems(true);
        try {
            const response = await axiosInstance.get(`/travel-order-items/?travel_order_id=${orderId}`);
            setOrderItems(response.data || []);
        } catch (error) {
            console.error('Error fetching order items:', error);
        } finally {
            setLoadingOrderItems(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setFormErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.name) errors.name = 'Cancellation Name is required';
        if (!formData.invoice_id) errors.invoice_id = 'Invoice is required';
        if (!formData.travel_order_id) errors.travel_order_id = 'Travel Order is required';
        if (!formData.order_line_item_id) errors.order_line_item_id = 'Order Line Item is required';

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSaving(true);
        try {
            const payload = {
                ...formData,
                invoice_id: parseInt(formData.invoice_id),
                payment_transaction_id: formData.payment_transaction_id ? parseInt(formData.payment_transaction_id) : null,
                travel_invoice_id: formData.travel_invoice_id ? parseInt(formData.travel_invoice_id) : null,
                travel_order_id: parseInt(formData.travel_order_id),
                order_line_item_id: parseInt(formData.order_line_item_id),
                requested_on: formData.requested_on ? new Date(formData.requested_on).toISOString() : null,
                amount_deduction: formData.amount_deduction ? parseFloat(formData.amount_deduction) : null,
                total_amount: formData.total_amount ? parseFloat(formData.total_amount) : null,
                refund_amount: formData.refund_amount ? parseFloat(formData.refund_amount) : null,
                created_by: user?.id,
                updated_by: user?.id
            };

            await onSave(payload);
            onClose();
        } catch (error) {
            console.error('Error saving Order Cancellation:', error);
            setToast({
                open: true,
                message: error.response?.data?.detail || 'Error saving Order Cancellation',
                severity: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <Modal open={isOpen} onClose={onClose}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '95%', sm: '90%', md: '900px' },
                    maxHeight: '90vh',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    <Box className="pearl-popup-header" sx={{
                        backgroundColor: colors.popupHeader.background,
                        px: 3,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <MDTypography variant="h6" color="white">Add New Order Cancellation</MDTypography>
                        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 4, overflowY: 'auto' }}>
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Cancellation Name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        error={!!formErrors.name}
                                        helperText={formErrors.name}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.invoice_id} required>
                                        <InputLabel>Invoice</InputLabel>
                                        <Select
                                            name="invoice_id"
                                            value={formData.invoice_id}
                                            onChange={handleInputChange}
                                            label="Invoice"

                                            disabled={loadingInvoices}
                                        >
                                            <MenuItem value=""><em>Select an Invoice</em></MenuItem>
                                            {invoices.map(inv => (
                                                <MenuItem key={inv.id} value={inv.id}>{inv.invoice_number || inv.name || inv.id}</MenuItem>
                                            ))}
                                        </Select>
                                        {formErrors.invoice_id && <MDTypography variant="caption" color="error">{formErrors.invoice_id}</MDTypography>}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.payment_transaction_id}>
                                        <InputLabel>Payment Transaction</InputLabel>
                                        <Select
                                            name="payment_transaction_id"
                                            value={formData.payment_transaction_id}
                                            onChange={handleInputChange}
                                            label="Payment Transaction"

                                            disabled={loadingPaymentTransactions}
                                        >
                                            <MenuItem value=""><em>Select Payment Transaction</em></MenuItem>
                                            {paymentTransactions.map(pt => (
                                                <MenuItem key={pt.id} value={pt.id}>{pt.name || pt.id}</MenuItem>
                                            ))}
                                        </Select>
                                        {formErrors.payment_transaction_id && <MDTypography variant="caption" color="error">{formErrors.payment_transaction_id}</MDTypography>}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.travel_invoice_id}>
                                        <InputLabel>Travel Invoice</InputLabel>
                                        <Select
                                            name="travel_invoice_id"
                                            value={formData.travel_invoice_id}
                                            onChange={handleInputChange}
                                            label="Travel Invoice"

                                            disabled={loadingTravelInvoices}
                                        >
                                            <MenuItem value=""><em>Select Travel Invoice</em></MenuItem>
                                            {travelInvoices.map(ti => (
                                                <MenuItem key={ti.id} value={ti.id}>{ti.name || ti.invoice_number || ti.id}</MenuItem>
                                            ))}
                                        </Select>
                                        {formErrors.travel_invoice_id && <MDTypography variant="caption" color="error">{formErrors.travel_invoice_id}</MDTypography>}
                                    </FormControl>
                                </Grid>

                                {!travelOrderId && (
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.travel_order_id} required>
                                            <InputLabel>Travel Order</InputLabel>
                                            <Select
                                                name="travel_order_id"
                                                value={formData.travel_order_id}
                                                onChange={handleInputChange}
                                                label="Travel Order"

                                                disabled={loadingTravelOrders}
                                            >
                                                <MenuItem value=""><em>Select a Travel Order</em></MenuItem>
                                                {travelOrders.map(to => (
                                                    <MenuItem key={to.id} value={to.id}>{to.name}</MenuItem>
                                                ))}
                                            </Select>
                                            {formErrors.travel_order_id && <MDTypography variant="caption" color="error">{formErrors.travel_order_id}</MDTypography>}
                                        </FormControl>
                                    </Grid>
                                )}

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.order_line_item_id} required>
                                        <InputLabel>Order Line Item</InputLabel>
                                        <Select
                                            name="order_line_item_id"
                                            value={formData.order_line_item_id}
                                            onChange={handleInputChange}
                                            label="Order Line Item"

                                            disabled={loadingOrderItems || !formData.travel_order_id}
                                        >
                                            <MenuItem value=""><em>Select an Order Line Item</em></MenuItem>
                                            {orderItems.map(oi => (
                                                <MenuItem key={oi.id} value={oi.id}>{oi.name}</MenuItem>
                                            ))}
                                        </Select>
                                        {formErrors.order_line_item_id && <MDTypography variant="caption" color="error">{formErrors.order_line_item_id}</MDTypography>}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" required>
                                        <InputLabel>Approval Status</InputLabel>
                                        <Select
                                            name="approval_status"
                                            value={formData.approval_status}
                                            onChange={handleInputChange}
                                            label="Approval Status"

                                        >
                                            <MenuItem value="Not Submitted">Not Submitted</MenuItem>
                                            <MenuItem value="Pending Approval">Pending Approval</MenuItem>
                                            <MenuItem value="Approved-Not Processed">Approved-Not Processed</MenuItem>
                                            <MenuItem value="Processed">Processed</MenuItem>
                                            <MenuItem value="Rejected">Rejected</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Cancellation Type</InputLabel>
                                        <Select
                                            name="cancellation_type"
                                            value={formData.cancellation_type}
                                            onChange={handleInputChange}
                                            label="Cancellation Type"

                                        >
                                            <MenuItem value=""><em>Select Type</em></MenuItem>
                                            <MenuItem value="Departure Date Change">Departure Date Change</MenuItem>
                                            <MenuItem value="Min. Passenger Not Met">Min. Passenger Not Met</MenuItem>
                                            <MenuItem value="Customer Cancellation">Customer Cancellation</MenuItem>
                                            <MenuItem value="Non Payment">Non Payment</MenuItem>
                                            <MenuItem value="Circumstantial">Circumstantial</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Status</InputLabel>
                                        <Select
                                            name="status"
                                            value={formData.status}
                                            onChange={handleInputChange}
                                            label="Status"

                                        >
                                            <MenuItem value="Active">Active</MenuItem>
                                            <MenuItem value="Not Submitted">Not Submitted</MenuItem>
                                            <MenuItem value="Submitted">Submitted</MenuItem>
                                            <MenuItem value="Approved">Approved</MenuItem>
                                            <MenuItem value="Rejected">Rejected</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Requested On"
                                        name="requested_on"
                                        type="datetime-local"
                                        value={formData.requested_on}
                                        onChange={handleInputChange}
                                        InputLabelProps={{ shrink: true }}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Amount Deduction"
                                        name="amount_deduction"
                                        type="number"
                                        value={formData.amount_deduction}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Total Amount"
                                        name="total_amount"
                                        type="number"
                                        value={formData.total_amount}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Refund Amount"
                                        name="refund_amount"
                                        type="number"
                                        value={formData.refund_amount}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Reject Reason"
                                        name="reject_reason"
                                        multiline
                                        rows={3}
                                        value={formData.reject_reason}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Comments"
                                        name="comments"
                                        multiline
                                        rows={3}
                                        value={formData.comments}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                            </Grid>

                            <MDBox mt={4} display="flex" justifyContent="flex-end">
                                <MDButton onClick={onClose} color="secondary" sx={{ mr: 2 }}>Cancel</MDButton>
                                <MDButton type="submit" variant="gradient" color="info" disabled={isSaving}>
                                    {isSaving ? <CircularProgress size={20} color="inherit" /> : 'Save Order Cancellation'}
                                </MDButton>
                            </MDBox>
                        </form>
                    </Box>
                </Box>
            </Modal>

            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={() => setToast({ ...toast, open: false })}
            >
                <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default NewOrderCancellationModal;

